import React, { useState, useEffect } from 'react';
import esgInvestorDashboardService from '../../../services/esg/esgInvestorDashboardService';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * ESG Investor Dashboard Component
 * 
 * Modern investor dashboard featuring:
 * - Real-time portfolio analytics with performance tracking
 * - ESG scoring and impact measurement
 * - Risk analysis and scenario modeling
 * - Investment opportunities pipeline
 * - Market intelligence and competitive analysis
 * - Interactive visualizations and data insights
 */
const ESGInvestorDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1Y');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
    const unsubscribe = esgInvestorDashboardService.subscribe((data) => {
      setDashboardData(data);
    });
    return unsubscribe;
  }, []);

  const loadDashboardData = async () => {
    try {
      setError(null);
      const data = await esgInvestorDashboardService.getInvestorDashboard();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load investor dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await esgInvestorDashboardService.refreshData();
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Investor Dashboard</h3>
          <p className="text-gray-600">Fetching portfolio and ESG analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'portfolio', name: 'Portfolio', icon: '💼' },
    { id: 'esg', name: 'ESG Analysis', icon: '🌱' },
    { id: 'risk', name: 'Risk', icon: '⚠️' },
    { id: 'opportunities', name: 'Opportunities', icon: '🎯' },
    { id: 'market', name: 'Market Intel', icon: '📈' }
  ];

  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white shadow-2xl">
        <div className="container-fluid py-8 px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                  <span className="text-3xl">💼</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-1">Investor Dashboard</h1>
                  <p className="text-blue-100 text-sm">ESG Portfolio Analytics & Investment Intelligence</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Quick Stats */}
              <div className="hidden lg:flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-xs text-blue-100">Portfolio Value</div>
                  <div className="text-lg font-bold">${dashboardData?.portfolio?.summary?.totalValue || '2.5B'}</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-xs text-blue-100">YTD Return</div>
                  <div className="text-lg font-bold text-green-300">+{dashboardData?.portfolio?.summary?.ytdReturn || '12.4'}%</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/30">
                  <div className="text-xs text-blue-100">ESG Score</div>
                  <div className="text-lg font-bold">{dashboardData?.esgAnalysis?.overallScore || 78}/100</div>
                </div>
              </div>
              <a 
                href="https://lively-island-0b6163f1e-preview.westus2.2.azurestaticapps.net/materiality" 
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-all text-white text-decoration-none flex items-center gap-2 shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>📊</span>
                <span>Data Collection</span>
              </a>
              <select 
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                <option value="1M">1 Month</option>
                <option value="3M">3 Months</option>
                <option value="6M">6 Months</option>
                <option value="1Y">1 Year</option>
                <option value="3Y">3 Years</option>
                <option value="5Y">5 Years</option>
              </select>
              <button 
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
              >
                {refreshing ? '⟳' : '🔄'} Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container-fluid px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-fluid px-6 py-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'portfolio' && renderPortfolio()}
        {activeTab === 'esg' && renderESG()}
        {activeTab === 'risk' && renderRisk()}
        {activeTab === 'opportunities' && renderOpportunities()}
        {activeTab === 'market' && renderMarket()}
      </div>
    </div>
  );

  function renderOverview() {
    const stats = [
      { 
        label: 'Assets Under Management', 
        value: '$2.5B', 
        change: '+8.2%', 
        trend: 'up', 
        icon: '💰', 
        color: 'from-blue-500 to-blue-600' 
      },
      { 
        label: 'Total Portfolio Return', 
        value: '+12.4%', 
        change: 'YTD', 
        trend: 'up', 
        icon: '📈', 
        color: 'from-green-500 to-green-600' 
      },
      { 
        label: 'ESG Score', 
        value: '78/100', 
        change: '+5 pts', 
        trend: 'up', 
        icon: '🌱', 
        color: 'from-emerald-500 to-emerald-600' 
      },
      { 
        label: 'Active Positions', 
        value: '156', 
        change: '+12', 
        trend: 'up', 
        icon: '🎯', 
        color: 'from-purple-500 to-purple-600' 
      }
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-xl shadow-lg`}>
                  <span className="text-2xl">{stat.icon}</span>
                </div>
                <span className={`px-3 py-1 ${
                  stat.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                } text-xs font-semibold rounded-full flex items-center gap-1`}>
                  {stat.trend === 'up' ? '↗' : '↘'} {stat.change}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Portfolio Performance */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Portfolio Performance</h3>
                <p className="text-sm text-gray-600">12-month trend analysis</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">↗ +12.4%</span>
            </div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generatePerformanceData()}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorValue)" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Asset Allocation */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Asset Allocation</h3>
                <p className="text-sm text-gray-600">Portfolio distribution</p>
              </div>
            </div>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Equities', value: 45 },
                      { name: 'Fixed Income', value: 25 },
                      { name: 'Real Estate', value: 15 },
                      { name: 'Alternatives', value: 10 },
                      { name: 'Cash', value: 5 }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ESG Scores & Risk Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ESG Scores */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">ESG Scores</h3>
            <div className="space-y-4">
              {[
                { label: 'Environmental', score: 82, color: 'green' },
                { label: 'Social', score: 76, color: 'blue' },
                { label: 'Governance', score: 75, color: 'purple' }
              ].map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">{item.label}</span>
                    <span className="font-bold text-gray-900">{item.score}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`bg-${item.color}-500 h-3 rounded-full transition-all`}
                      style={{ width: `${item.score}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Metrics */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Risk Metrics</h3>
            <div className="space-y-4">
              {[
                { label: 'Portfolio Beta', value: '0.92', status: 'Low' },
                { label: 'Sharpe Ratio', value: '1.85', status: 'High' },
                { label: 'Max Drawdown', value: '-8.3%', status: 'Low' }
              ].map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-700">{metric.label}</div>
                    <div className="text-lg font-bold text-gray-900">{metric.value}</div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    metric.status === 'High' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {metric.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Holdings */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Top Holdings</h3>
            <div className="space-y-3">
              {[
                { name: 'Apple Inc.', ticker: 'AAPL', allocation: 8.5, return: 15.2 },
                { name: 'Microsoft Corp.', ticker: 'MSFT', allocation: 7.2, return: 12.8 },
                { name: 'Tesla Inc.', ticker: 'TSLA', allocation: 5.8, return: 22.4 },
                { name: 'Amazon.com Inc.', ticker: 'AMZN', allocation: 5.1, return: 9.6 }
              ].map((holding, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 text-sm">{holding.name}</div>
                    <div className="text-xs text-gray-600">{holding.ticker} • {holding.allocation}%</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${holding.return > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      +{holding.return}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Insights Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="bg-blue-500 p-3 rounded-xl">
              <span className="text-2xl">💡</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Portfolio Insights</h3>
              <p className="text-blue-800 text-sm mb-3">
                Your portfolio is outperforming the benchmark by 3.2% YTD with a strong ESG score of 78/100. 
                Consider increasing allocation to renewable energy sector for enhanced ESG performance.
              </p>
              <div className="flex gap-3">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all">
                  View Recommendations
                </button>
                <button className="bg-white hover:bg-blue-50 text-blue-600 border border-blue-300 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderPortfolio() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Portfolio Analysis</h2>
          <p className="text-gray-600 mb-6">Comprehensive portfolio holdings and performance metrics</p>

          {/* Portfolio Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Value', value: '$2.5B', icon: '💰' },
              { label: 'Holdings', value: '156', icon: '📊' },
              { label: 'Sectors', value: '11', icon: '🏢' },
              { label: 'Avg Position', value: '$16M', icon: '📈' }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{item.value}</div>
              </div>
            ))}
          </div>

          {/* Sector Allocation */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Sector Allocation</h3>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { sector: 'Technology', allocation: 28, benchmark: 25 },
                  { sector: 'Healthcare', allocation: 18, benchmark: 15 },
                  { sector: 'Financials', allocation: 15, benchmark: 18 },
                  { sector: 'Consumer', allocation: 12, benchmark: 14 },
                  { sector: 'Industrials', allocation: 10, benchmark: 11 },
                  { sector: 'Energy', allocation: 8, benchmark: 10 },
                  { sector: 'Utilities', allocation: 5, benchmark: 4 },
                  { sector: 'Real Estate', allocation: 4, benchmark: 3 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="sector" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="allocation" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Portfolio %" />
                  <Bar dataKey="benchmark" fill="#94a3b8" radius={[8, 8, 0, 0]} name="Benchmark %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderESG() {
    return (
      <div className="space-y-6">
        {/* ESG Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { 
              category: 'Environmental', 
              score: 82, 
              change: '+5', 
              color: 'from-green-500 to-emerald-600',
              icon: '🌍',
              metrics: [
                { label: 'Carbon Footprint', value: 'Low', status: 'good' },
                { label: 'Renewable Energy', value: '65%', status: 'good' },
                { label: 'Waste Management', value: 'Medium', status: 'warning' }
              ]
            },
            { 
              category: 'Social', 
              score: 76, 
              change: '+3', 
              color: 'from-blue-500 to-indigo-600',
              icon: '👥',
              metrics: [
                { label: 'Employee Satisfaction', value: 'High', status: 'good' },
                { label: 'Diversity & Inclusion', value: '72%', status: 'good' },
                { label: 'Community Impact', value: 'Medium', status: 'warning' }
              ]
            },
            { 
              category: 'Governance', 
              score: 75, 
              change: '+2', 
              color: 'from-purple-500 to-pink-600',
              icon: '⚖️',
              metrics: [
                { label: 'Board Independence', value: '85%', status: 'good' },
                { label: 'Executive Pay', value: 'Fair', status: 'good' },
                { label: 'Shareholder Rights', value: 'Strong', status: 'good' }
              ]
            }
          ].map((esg, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className={`bg-gradient-to-br ${esg.color} p-3 rounded-xl`}>
                  <span className="text-2xl">{esg.icon}</span>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  {esg.change} pts
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{esg.category}</h3>
              <div className="text-4xl font-bold text-gray-900 mb-4">{esg.score}/100</div>
              
              <div className="space-y-3">
                {esg.metrics.map((metric, midx) => (
                  <div key={midx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{metric.label}</span>
                    <span className={`font-semibold ${
                      metric.status === 'good' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ESG Radar Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">ESG Performance Radar</h3>
          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={[
                { metric: 'Carbon Emissions', portfolio: 82, benchmark: 70 },
                { metric: 'Renewable Energy', portfolio: 85, benchmark: 65 },
                { metric: 'Water Usage', portfolio: 78, benchmark: 72 },
                { metric: 'Employee Rights', portfolio: 88, benchmark: 75 },
                { metric: 'Diversity', portfolio: 76, benchmark: 68 },
                { metric: 'Board Independence', portfolio: 85, benchmark: 80 },
                { metric: 'Ethics & Compliance', portfolio: 90, benchmark: 82 }
              ]}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name="Portfolio" dataKey="portfolio" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
                <Radar name="Benchmark" dataKey="benchmark" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.3} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  function renderRisk() {
    return (
      <div className="space-y-6">
        {/* Risk Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { label: 'Overall Risk', value: 'Medium', level: 60, color: 'from-yellow-500 to-orange-600', icon: '⚠️' },
            { label: 'Market Risk', value: 'Low-Med', level: 45, color: 'from-green-500 to-emerald-600', icon: '📊' },
            { label: 'ESG Risk', value: 'Low', level: 25, color: 'from-blue-500 to-indigo-600', icon: '🌱' },
            { label: 'Concentration', value: 'Medium', level: 55, color: 'from-purple-500 to-pink-600', icon: '🎯' }
          ].map((risk, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className={`bg-gradient-to-br ${risk.color} p-3 rounded-xl w-fit mb-3`}>
                <span className="text-2xl">{risk.icon}</span>
              </div>
              <h3 className="text-sm text-gray-600 mb-2">{risk.label}</h3>
              <div className="text-2xl font-bold text-gray-900 mb-3">{risk.value}</div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`bg-gradient-to-r ${risk.color} h-2 rounded-full transition-all`}
                  style={{ width: `${risk.level}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Risk Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk Factors */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Key Risk Factors</h3>
            <div className="space-y-4">
              {[
                { factor: 'Interest Rate Risk', level: 'Low', impact: 'Minimal', score: 25 },
                { factor: 'Currency Risk', level: 'Medium', impact: 'Moderate', score: 55 },
                { factor: 'Liquidity Risk', level: 'Low', impact: 'Low', score: 30 },
                { factor: 'Regulatory Risk', level: 'Medium', impact: 'Significant', score: 65 },
                { factor: 'Geopolitical Risk', level: 'High', impact: 'Moderate', score: 75 }
              ].map((item, idx) => (
                <div key={idx} className="border-l-4 border-blue-500 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{item.factor}</h4>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      item.level === 'Low' ? 'bg-green-100 text-green-700' :
                      item.level === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {item.level}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 mb-2">
                    <span>Impact: {item.impact}</span>
                    <span>{item.score}/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        item.level === 'Low' ? 'bg-green-500' :
                        item.level === 'Medium' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${item.score}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stress Test Scenarios */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Stress Test Scenarios</h3>
            <div className="space-y-4">
              {[
                { scenario: 'Market Crash (-30%)', impact: '-$750M', probability: '5%', severity: 'High' },
                { scenario: 'Recession', impact: '-$500M', probability: '15%', severity: 'Medium' },
                { scenario: 'ESG Downgrade', impact: '-$150M', probability: '10%', severity: 'Low' },
                { scenario: 'Regulatory Change', impact: '-$250M', probability: '20%', severity: 'Medium' }
              ].map((test, idx) => (
                <div key={idx} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 text-sm">{test.scenario}</h4>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      test.severity === 'High' ? 'bg-red-100 text-red-700' :
                      test.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {test.severity}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-gray-600">Portfolio Impact</div>
                      <div className="font-bold text-red-600">{test.impact}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Probability</div>
                      <div className="font-bold text-gray-900">{test.probability}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderOpportunities() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Investment Opportunities</h2>
          
          {/* Opportunity Pipeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                company: 'SolarTech Industries',
                sector: 'Renewable Energy',
                stage: 'Due Diligence',
                investment: '$50M',
                esgScore: 92,
                expectedReturn: 18.5,
                risk: 'Medium'
              },
              {
                company: 'Green Mobility Corp',
                sector: 'Transportation',
                stage: 'Under Review',
                investment: '$35M',
                esgScore: 88,
                expectedReturn: 15.2,
                risk: 'Low-Medium'
              },
              {
                company: 'Sustainable Foods LLC',
                sector: 'Agriculture',
                stage: 'Negotiation',
                investment: '$25M',
                esgScore: 85,
                expectedReturn: 12.8,
                risk: 'Low'
              },
              {
                company: 'CleanTech Solutions',
                sector: 'Technology',
                stage: 'Analysis',
                investment: '$60M',
                esgScore: 90,
                expectedReturn: 20.3,
                risk: 'Medium-High'
              },
              {
                company: 'EcoBuilders Group',
                sector: 'Real Estate',
                stage: 'Due Diligence',
                investment: '$45M',
                esgScore: 86,
                expectedReturn: 14.7,
                risk: 'Medium'
              },
              {
                company: 'Water Tech Inc',
                sector: 'Utilities',
                stage: 'Under Review',
                investment: '$30M',
                esgScore: 89,
                expectedReturn: 11.5,
                risk: 'Low'
              }
            ].map((opp, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{opp.company}</h3>
                    <p className="text-xs text-gray-600">{opp.sector}</p>
                  </div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                    {opp.stage}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-600">Investment</div>
                    <div className="font-bold text-gray-900">{opp.investment}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs text-gray-600">ESG Score</div>
                    <div className="font-bold text-green-600">{opp.esgScore}/100</div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-600">Expected Return:</span>
                  <span className="font-bold text-green-600">+{opp.expectedReturn}%</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Risk Level:</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    opp.risk.includes('Low') ? 'bg-green-100 text-green-700' :
                    opp.risk.includes('Medium') ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {opp.risk}
                  </span>
                </div>

                <button className="w-full mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all">
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderMarket() {
    return (
      <div className="space-y-6">
        {/* Market Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { index: 'S&P 500', value: '4,567.89', change: '+1.2%', trend: 'up' },
            { index: 'MSCI ESG', value: '1,234.56', change: '+1.8%', trend: 'up' },
            { index: 'Green Bonds', value: '987.65', change: '+0.5%', trend: 'up' },
            { index: 'Renewable ETF', value: '456.78', change: '-0.3%', trend: 'down' }
          ].map((market, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="text-sm text-gray-600 mb-2">{market.index}</h3>
              <div className="text-2xl font-bold text-gray-900 mb-1">{market.value}</div>
              <span className={`text-sm font-semibold ${
                market.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {market.trend === 'up' ? '↗' : '↘'} {market.change}
              </span>
            </div>
          ))}
        </div>

        {/* Market Intelligence */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Market Intelligence</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trending Sectors */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Trending ESG Sectors</h3>
              <div className="space-y-3">
                {[
                  { sector: 'Renewable Energy', growth: '+24%', sentiment: 'Very Positive' },
                  { sector: 'Electric Vehicles', growth: '+18%', sentiment: 'Positive' },
                  { sector: 'Sustainable Agriculture', growth: '+15%', sentiment: 'Positive' },
                  { sector: 'Green Technology', growth: '+12%', sentiment: 'Neutral' }
                ].map((sector, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-green-50 rounded-lg border border-gray-200">
                    <div>
                      <div className="font-semibold text-gray-900">{sector.sector}</div>
                      <div className="text-sm text-gray-600">{sector.sentiment}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{sector.growth}</div>
                      <div className="text-xs text-gray-500">YTD</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Market News */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Latest Market News</h3>
              <div className="space-y-3">
                {[
                  { title: 'New ESG Regulations Announced', time: '2 hours ago', impact: 'High' },
                  { title: 'Renewable Energy Index Hits Record', time: '5 hours ago', impact: 'Medium' },
                  { title: 'Corporate Sustainability Reports Due', time: '1 day ago', impact: 'Medium' },
                  { title: 'Green Bond Market Expands', time: '2 days ago', impact: 'Low' }
                ].map((news, idx) => (
                  <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm flex-1">{news.title}</h4>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                        news.impact === 'High' ? 'bg-red-100 text-red-700' :
                        news.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {news.impact}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{news.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions
  function generatePerformanceData() {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let value = 2000;
    return months.map(month => {
      value += (Math.random() - 0.3) * 200 + 50;
      return { month, value: Math.round(value) };
    });
  }
};

export default ESGInvestorDashboard;
